import { BehaviorSubject, Observable, EMPTY, isObservable, concat, MonoTypeOperatorFunction } from 'rxjs';
import { finalize } from 'rxjs/operators';

/**
 * Tracks async operations and aggregates them to a binary result, busy or not busy.
 */
export class AsyncTracker {
  get busy(): boolean {
    return this._busy$.value;
  }

  readonly pipe: <T>(source: Observable<T>) => Observable<T>; // MonoTypeOperatorFunction<T> but we cant <T>MonoTypeOperatorFunction<T>

  busy$: Observable<boolean>;

  private _busy$ = new BehaviorSubject<boolean>(false);
  private count = 0;

  constructor(startBusy = false) {
    this.busy$ = this._busy$.asObservable();
    this.pipe = trackWith(this);
    if (startBusy) {
      this.updateAndEmit(1);
    }
  }

  /**
   * Manual activation of the tracker.
   * Returns a functions that when called signals the end of the tracking.
   *
   * Manual tracking is immidiate.
   */
  track(): () => void;
  /**
   * Tracks an async operation wrapped within a promise.
   * Returns a promise the resolves or rejects the value from the original Promise.
   *
   * Promise tracking is immidiate.
   */
  track<T>(promise: Promise<T>): Promise<T>;
  /**
   * Tracks an async operation wrapped within an observable.
   * Returns a new Observable that emits the items emitted by the source observable.
   *
   * This method is an outer version of a pipe, i.e. it expects a cold observable.
   *
   * Observable tracking is not immidiate.
   * State will increase only when the observable is subscribed to and will decrease only when the observable complete's or error's.
   */
  track<T>(obs: Observable<T>): Observable<T>;
  track<T>(obs?: Observable<T> | Promise<T>): Observable<T> | Promise<T> | (() => void) {
    const increase = () => this.updateAndEmit(1);
    const decrease = () => this.updateAndEmit(-1);
    if (!obs) {
      increase();
      return decrease;
    } else if (isObservable(obs)) {
      obs = obs.pipe(finalize(decrease));
      return concat(EMPTY.pipe(finalize(increase)), obs);
    } else {
      increase();
      return obs
        .then((value: T) => {
          decrease();
          return value;
        })
        .catch((err) => {
          decrease();
          throw err;
        });
    }
  }

  updateAndEmit(incOrDec: -1 | 1): void {
    this.count = this.count + incOrDec;
    const busy = this.count > 0;
    if (this._busy$.value !== busy) {
      this._busy$.next(busy);
    }
  }
}

export function trackWith<T>(tracker: AsyncTracker): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>) => tracker.track(source);
}
